import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, where, serverTimestamp, Timestamp, doc, getDoc, runTransaction } from 'firebase/firestore';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, TextField, Divider, ToggleButtonGroup, ToggleButton, Stack, IconButton, Chip, CircularProgress, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import PartSelectionSection from './PartSelectionSection';
import { camParts, seramikParts, ppfParts, aracTurleri } from './PreInterviewConstants';
import { notlarToText } from './PreInterviewNotes';

const IsEmriOlustur = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [loading, setLoading] = useState(true);

    // Ön görüşmeden gelen veri
    const preInterviewData = location.state;

    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [allServices, setAllServices] = useState([]);
    const [uygulamaTurleri, setUygulamaTurleri] = useState([]);

    // Pre-interview ve appointment referansları
    const [preInterviewId, setPreInterviewId] = useState(null);
    const [appointmentId, setAppointmentId] = useState(null);

    // Uygulama verileri
    // GÜNCELLENDİ: 'paketlerData' eklendi
    const [paketlerData, setPaketlerData] = useState([]);
    const [camFilmiData, setCamFilmiData] = useState([]);
    const [ppfData, setPpfData] = useState([]);
    const [seramikData, setSeramikData] = useState([]);
    const [gocukData, setGocukData] = useState({ aktif: false, fiyat: 0, aciklama: '' });
    const [yikamaData, setYikamaData] = useState({ aktif: false, fiyat: 0 });
    const [digerHizmetler, setDigerHizmetler] = useState([{ aciklama: '', fiyat: 0 }]);

    const [toplamTutar, setToplamTutar] = useState(0);
    const [odenenTutar, setOdenenTutar] = useState(0);
    const [isEmriNotlari, setIsEmriNotlari] = useState('');
    const [teslimTarihi, setTeslimTarihi] = useState(null);
    // Kademeli fiyatlandırma için araç türü (ön görüşmeden taşınır, yoksa aracın 'tip' alanı, o da yoksa elle seçilir)
    const [aracTuru, setAracTuru] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Filtrelenmiş hizmetler
    // GÜNCELLENDİ: 'paketServices' eklendi
    const paketServices = allServices.filter(s => s.tip === 'Paketler');
    const filmServices = allServices.filter(s => s.tip === 'Cam Filmi');
    const ppfServices = allServices.filter(s => s.tip === 'PPF Kaplama');
    const seramikServices = allServices.filter(s => s.tip === 'Seramik Kaplama');

    useEffect(() => {
        const unsub = onSnapshot(query(collection(db, "customers"), orderBy("adSoyad", "asc")), (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, []);

    useEffect(() => {
        if (!selectedCustomerId) { setVehicles([]); return; }
        const unsub = onSnapshot(query(collection(db, "vehicles"), where("musteriId", "==", selectedCustomerId)), (snapshot) => {
            setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, [selectedCustomerId]);

    useEffect(() => {
        const unsub = onSnapshot(query(collection(db, "services"), orderBy("hizmetAdi", "asc")), (snapshot) => {
            setAllServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, []);

    // Ön görüşmeden veya randevudan gelen verilerle formu doldur
    useEffect(() => {
        if ((preInterviewData?.fromPreInterview || preInterviewData?.fromAppointment) && !isEditMode) {
            // Müşteri seç (aracı müşteri yüklendikten sonra seçilecek)
            if (preInterviewData.musteriId) setSelectedCustomerId(preInterviewData.musteriId);
            if (preInterviewData.preInterviewId) setPreInterviewId(preInterviewData.preInterviewId);
            if (preInterviewData.appointmentId) setAppointmentId(preInterviewData.appointmentId);

            // Teslim tarihi - ISO string'den Date'e
            if (preInterviewData.teslimTarihi) {
                try {
                    const date = new Date(preInterviewData.teslimTarihi);
                    if (!isNaN(date.getTime())) {
                        setTeslimTarihi(date);
                    }
                } catch (e) {
                    console.error("Teslim tarihi parse hatası:", e);
                }
            }

            // Notlar (ön görüşme notu dizi formatında da gelebilir → düz metne indir)
            const gelenNotlar = notlarToText(preInterviewData.notlar);
            if (gelenNotlar) setIsEmriNotlari(gelenNotlar);

            // Araç türü (teklifteki kademeli fiyatın iş emrinde de geçerli olması için)
            if (preInterviewData.aracTuru) setAracTuru(preInterviewData.aracTuru);

            // Hizmetleri eşleştir
            const uygulamalar = [];
            const ilgilenilenHizmetler = preInterviewData.ilgilenilenHizmetler || [];
            if (ilgilenilenHizmetler.includes('Paketler')) uygulamalar.push('paketler');
            if (ilgilenilenHizmetler.includes('Cam Filmi')) uygulamalar.push('camFilmi');
            if (ilgilenilenHizmetler.includes('PPF Kaplama')) uygulamalar.push('ppf');
            if (ilgilenilenHizmetler.includes('Seramik Kaplama') || ilgilenilenHizmetler.includes('Seratik Kaplama')) uygulamalar.push('seramik');
            if (ilgilenilenHizmetler.includes('Göçük Düzeltme')) uygulamalar.push('gocuk');
            if (ilgilenilenHizmetler.includes('Yıkama')) uygulamalar.push('yikama');
            if (ilgilenilenHizmetler.includes('Detay Temizlik') || ilgilenilenHizmetler.includes('Diğer')) uygulamalar.push('diger');

            // GÜNCELLENDİ: Paket içeriğindeki hizmetleri de otomatik ekle
            if (preInterviewData.uygulamalar && preInterviewData.uygulamalar.paketler) {
                const paketler = preInterviewData.uygulamalar.paketler;
                paketler.forEach(paket => {
                    if (paket.icindekiHizmetler) {
                        paket.icindekiHizmetler.forEach(altHizmet => {
                            // Alt hizmetin tipine göre uygulama listesine ekle
                            // Not: Alt hizmet tipleri "PPF Kaplama", "Cam Filmi" vb. şeklinde geliyor olabilir.
                            // PartSelectionSection.js içinde tipine göre filtreleme yapılıyor.
                            if (altHizmet.tip === 'Cam Filmi' && !uygulamalar.includes('camFilmi')) uygulamalar.push('camFilmi');
                            if (altHizmet.tip === 'PPF Kaplama' && !uygulamalar.includes('ppf')) uygulamalar.push('ppf');
                            if ((altHizmet.tip === 'Seramik Kaplama' || altHizmet.tip === 'Seratik Kaplama') && !uygulamalar.includes('seramik')) uygulamalar.push('seramik');
                            if (altHizmet.tip === 'Göçük Düzeltme' && !uygulamalar.includes('gocuk')) uygulamalar.push('gocuk');
                            if (altHizmet.tip === 'Yıkama' && !uygulamalar.includes('yikama')) uygulamalar.push('yikama');
                        });
                    }
                });
            }

            // Uygulama verilerini doldur
            if (preInterviewData.uygulamalar) {
                if (preInterviewData.uygulamalar.paketler) setPaketlerData(preInterviewData.uygulamalar.paketler);
                if (preInterviewData.uygulamalar.camFilmi) setCamFilmiData(preInterviewData.uygulamalar.camFilmi);
                if (preInterviewData.uygulamalar.ppf) setPpfData(preInterviewData.uygulamalar.ppf);
                if (preInterviewData.uygulamalar.seramik) setSeramikData(preInterviewData.uygulamalar.seramik);

                // Ön görüşmede "basit hizmetler" (Yıkama / Göçük / Detay Temizlik / Diğer) tek listede tutulur.
                // Aktarılmazsa teklifteki bu kalemler iş emrinde kaybolur ve toplam tutar ayrışır.
                const basitler = preInterviewData.uygulamalar.diger || [];
                if (basitler.length) {
                    const gocukKalemleri = basitler.filter(i => i.tip === 'Göçük Düzeltme');
                    const yikamaKalemleri = basitler.filter(i => i.tip === 'Yıkama');
                    const digerKalemleri = basitler.filter(i => i.tip !== 'Göçük Düzeltme' && i.tip !== 'Yıkama');

                    if (gocukKalemleri.length) {
                        setGocukData({
                            aktif: true,
                            fiyat: gocukKalemleri.reduce((s, i) => s + Number(i.fiyat || 0), 0),
                            aciklama: gocukKalemleri.map(i => i.hizmetAdi).filter(Boolean).join(', ')
                        });
                        if (!uygulamalar.includes('gocuk')) uygulamalar.push('gocuk');
                    }
                    if (yikamaKalemleri.length) {
                        setYikamaData({ aktif: true, fiyat: yikamaKalemleri.reduce((s, i) => s + Number(i.fiyat || 0), 0) });
                        if (!uygulamalar.includes('yikama')) uygulamalar.push('yikama');
                    }
                    if (digerKalemleri.length) {
                        setDigerHizmetler(digerKalemleri.map(i => ({
                            aciklama: i.hizmetAdi || i.tip || 'Hizmet',
                            fiyat: Number(i.fiyat || 0)
                        })));
                        if (!uygulamalar.includes('diger')) uygulamalar.push('diger');
                    }
                }
            }

            setUygulamaTurleri(uygulamalar);

            setLoading(false);
        }
    }, [preInterviewData, isEditMode]);

    // Araç türü: elle seçilmediyse seçili aracın 'tip' alanından türet (yalnızca yeni iş emrinde)
    useEffect(() => {
        if (isEditMode || aracTuru || !selectedVehicleId) return;
        const vehicle = vehicles.find(v => v.id === selectedVehicleId);
        if (vehicle?.tip) setAracTuru(vehicle.tip);
    }, [selectedVehicleId, vehicles, aracTuru, isEditMode]);

    // Araç seçimi - vehicles yüklendikten sonra
    useEffect(() => {
        if (preInterviewData?.aracId && vehicles.length > 0) {
            const aracMevcut = vehicles.find(v => v.id === preInterviewData.aracId);
            if (aracMevcut) {
                setSelectedVehicleId(preInterviewData.aracId);
            }
        }
    }, [vehicles, preInterviewData]);

    useEffect(() => {
        if (isEditMode) {
            setLoading(true);
            const fetchWorkOrder = async () => {
                const docRef = doc(db, "workOrders", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setSelectedCustomerId(data.musteriId);
                    setSelectedVehicleId(data.aracId);
                    setTeslimTarihi(data.teslimTarihi ? data.teslimTarihi.toDate() : null);

                    const uygulamalar = [];
                    // GÜNCELLENDİ: 'paketler' kontrolü eklendi
                    if (data.uygulamalar?.paketler?.length > 0) uygulamalar.push('paketler');
                    if (data.uygulamalar?.camFilmi?.length > 0) uygulamalar.push('camFilmi');
                    if (data.uygulamalar?.ppf?.length > 0) uygulamalar.push('ppf');
                    if (data.uygulamalar?.seramik?.length > 0) uygulamalar.push('seramik');
                    if (data.uygulamalar?.gocuk?.aktif) uygulamalar.push('gocuk');
                    if (data.uygulamalar?.yikama?.aktif) uygulamalar.push('yikama');
                    if (data.uygulamalar?.diger?.length > 0) uygulamalar.push('diger');
                    setUygulamaTurleri(uygulamalar);

                    // GÜNCELLENDİ: 'paketlerData' yüklendi
                    setPaketlerData(data.uygulamalar?.paketler || []);
                    setCamFilmiData(data.uygulamalar?.camFilmi || []);
                    setPpfData(data.uygulamalar?.ppf || []);
                    setSeramikData(data.uygulamalar?.seramik || []);
                    setGocukData(data.uygulamalar?.gocuk || { aktif: false, fiyat: 0, aciklama: '' });
                    setYikamaData(data.uygulamalar?.yikama || { aktif: false, fiyat: 0 });
                    setDigerHizmetler(data.uygulamalar?.diger?.length ? data.uygulamalar.diger : [{ aciklama: '', fiyat: 0 }]);
                    setOdenenTutar(data.odenenTutar || 0);
                    setIsEmriNotlari(data.isEmriNotlari || '');
                    setPreInterviewId(data.preInterviewId || null);
                    setAracTuru(data.aracTuru || '');
                } else {
                    alert("Düzenlenecek iş emri bulunamadı!");
                    navigate('/is-emirleri');
                }
                setLoading(false);
            };
            fetchWorkOrder();
        } else if (!preInterviewData?.fromPreInterview) {
            setLoading(false);
        }
    }, [id, isEditMode, navigate, preInterviewData]);

    useEffect(() => {
        const calcTotal = (arr) => arr.reduce((sum, p) => {
            if (p.satisTipi === 'paket') return sum + (p.paketFiyat || p.fiyat || 0);
            return sum + (p.fiyat || 0);
        }, 0);
        const paketTotal = uygulamaTurleri.includes('paketler') ? calcTotal(paketlerData) : 0;
        const camFilmiTotal = uygulamaTurleri.includes('camFilmi') ? calcTotal(camFilmiData) : 0;
        const ppfTotal = uygulamaTurleri.includes('ppf') ? calcTotal(ppfData) : 0;
        const seramikTotal = uygulamaTurleri.includes('seramik') ? calcTotal(seramikData) : 0;
        const gocukTotal = uygulamaTurleri.includes('gocuk') ? Number(gocukData.fiyat || 0) : 0;
        const yikamaTotal = uygulamaTurleri.includes('yikama') ? Number(yikamaData.fiyat || 0) : 0;
        const digerTotal = uygulamaTurleri.includes('diger') ? digerHizmetler.reduce((sum, h) => sum + Number(h.fiyat || 0), 0) : 0;

        setToplamTutar(paketTotal + camFilmiTotal + ppfTotal + seramikTotal + gocukTotal + yikamaTotal + digerTotal);
    }, [paketlerData, camFilmiData, ppfData, seramikData, gocukData, yikamaData, digerHizmetler, uygulamaTurleri]);

    const handleUygulamaChange = (e, newUygulamalar) => { setUygulamaTurleri(newUygulamalar); };
    const handleAddDiger = () => setDigerHizmetler([...digerHizmetler, { aciklama: '', fiyat: 0 }]);
    const handleDigerChange = (index, field, value) => { const newData = [...digerHizmetler]; newData[index][field] = value; setDigerHizmetler(newData); };
    const handleRemoveDiger = (index) => { const newData = [...digerHizmetler]; newData.splice(index, 1); setDigerHizmetler(newData); };

    const handleSubmit = async () => {
        if (!selectedCustomerId || !selectedVehicleId || uygulamaTurleri.length === 0 || !teslimTarihi) {
            alert("Lütfen Müşteri, Araç, Teslim Tarihi ve en az bir Uygulama seçimi yapınız.");
            return;
        }
        const customer = customers.find(c => c.id === selectedCustomerId);
        const vehicle = vehicles.find(v => v.id === selectedVehicleId);

        const currentToplamTutar = toplamTutar;
        const currentOdenenTutar = Number(odenenTutar) || 0;
        const currentKalanTutar = currentToplamTutar - currentOdenenTutar;

        // Ortak alanlar. odenenTutar/kalanTutar bilerek DIŞARIDA: düzenleme modunda bu iki alan
        // formdan değil, transaction içinde okunan güncel dokümandan türetilir (lost-update önlemi).
        const workOrderData = {
            musteriId: customer.id, musteriAdSoyad: customer.adSoyad, musteriTelefon: customer.telefon,
            aracId: vehicle.id, aracPlaka: vehicle.plaka || '', aracMarkaModel: `${vehicle.marka} ${vehicle.model}`,
            aracTuru: aracTuru || '',
            toplamTutar: currentToplamTutar,
            isEmriNotlari,
            teslimTarihi: Timestamp.fromDate(teslimTarihi),
            preInterviewId: preInterviewId || null,
            uygulamalar: {
                // GÜNCELLENDİ: Paketler kaydediliyor
                paketler: uygulamaTurleri.includes('paketler') ? paketlerData : [],
                camFilmi: uygulamaTurleri.includes('camFilmi') ? camFilmiData : [],
                ppf: uygulamaTurleri.includes('ppf') ? ppfData : [],
                seramik: uygulamaTurleri.includes('seramik') ? seramikData : [],
                gocuk: uygulamaTurleri.includes('gocuk') ? { ...gocukData, aktif: true } : { aktif: false, fiyat: 0, aciklama: '' },
                yikama: uygulamaTurleri.includes('yikama') ? { ...yikamaData, aktif: true } : { aktif: false, fiyat: 0 },
                diger: uygulamaTurleri.includes('diger') ? digerHizmetler.filter(h => h.aciklama) : [],
            }
        };

        setIsSaving(true);
        try {
            if (isEditMode) {
                const workOrderRef = doc(db, "workOrders", id);
                const customerDocRef = doc(db, "customers", customer.id);

                // Düzenleme cariyi FARK (delta) ile senkronize eder. Bu blok v1'de vardı,
                // v2 kopyalamasında kaybolmuştu (bkz. acc179b:200-228) — 2026-06-25 regresyonu.
                await runTransaction(db, async (transaction) => {
                    const workOrderDoc = await transaction.get(workOrderRef);
                    if (!workOrderDoc.exists()) throw new Error("İş emri bulunamadı!");
                    const oldWorkOrderData = workOrderDoc.data();

                    const customerDoc = await transaction.get(customerDocRef);
                    if (!customerDoc.exists()) throw new Error("Müşteri belgesi bulunamadı!");

                    // Müşteri değiştirilmiş olabilir: eski müşterinin dokümanı da okunmalı (tüm okumalar yazmalardan önce)
                    const musteriDegisti = oldWorkOrderData.musteriId && oldWorkOrderData.musteriId !== customer.id;
                    const oldCustomerRef = musteriDegisti ? doc(db, "customers", oldWorkOrderData.musteriId) : null;
                    const oldCustomerDoc = oldCustomerRef ? await transaction.get(oldCustomerRef) : null;

                    // Ödenen tutar YALNIZCA payments akışından değişir; formdaki değer yok sayılır.
                    const guncelOdenen = oldWorkOrderData.odenenTutar || 0;
                    const yeniKalan = currentToplamTutar - guncelOdenen;

                    if (musteriDegisti) {
                        // Eski müşteriden iş emrinin TAMAMINI geri al, yeni müşteriye tamamını ekle.
                        // (Eski müşteri dokümanı silinmişse geri alınacak yer yoktur; yeni müşteriye tam tutar eklenir.)
                        if (oldCustomerDoc?.exists()) {
                            const oldCust = oldCustomerDoc.data();
                            transaction.update(oldCustomerRef, {
                                toplamSatis: (oldCust.toplamSatis || 0) - (oldWorkOrderData.toplamTutar || 0),
                                toplamOdeme: (oldCust.toplamOdeme || 0) - (oldWorkOrderData.odenenTutar || 0),
                                toplamBakiye: (oldCust.toplamBakiye || 0) - (oldWorkOrderData.kalanTutar || 0)
                            });
                        }
                        const newCust = customerDoc.data();
                        transaction.update(customerDocRef, {
                            toplamSatis: (newCust.toplamSatis || 0) + currentToplamTutar,
                            toplamOdeme: (newCust.toplamOdeme || 0) + guncelOdenen,
                            toplamBakiye: (newCust.toplamBakiye || 0) + yeniKalan
                        });
                    } else {
                        const customerData = customerDoc.data();
                        const toplamFark = currentToplamTutar - (oldWorkOrderData.toplamTutar || 0);
                        const odenenFark = guncelOdenen - (oldWorkOrderData.odenenTutar || 0); // her zaman 0, açıklık için duruyor
                        const kalanFark = yeniKalan - (oldWorkOrderData.kalanTutar || 0);

                        transaction.update(customerDocRef, {
                            toplamSatis: (customerData.toplamSatis || 0) + toplamFark,
                            toplamOdeme: (customerData.toplamOdeme || 0) + odenenFark,
                            toplamBakiye: (customerData.toplamBakiye || 0) + kalanFark
                        });
                    }

                    transaction.update(workOrderRef, {
                        ...workOrderData,
                        odenenTutar: guncelOdenen,
                        kalanTutar: yeniKalan
                    });
                });

                alert("İş emri güncellendi ve müşteri bakiyesi senkronize edildi.");
                navigate(`/is-emri/${id}`);
            } else {
                const customerDocRef = doc(db, "customers", customer.id);
                const workOrdersColRef = collection(db, "workOrders");

                await runTransaction(db, async (transaction) => {
                    const customerDoc = await transaction.get(customerDocRef);
                    if (!customerDoc.exists()) {
                        throw new Error("Müşteri belgesi bulunamadı!");
                    }

                    // Mükerrer iş emri koruması: randevu transaction İÇİNDE okunur.
                    // Çift tıklama veya geri tuşuyla forma dönüş, aynı randevudan
                    // ikinci bir iş emri açıp cariyi iki kez şişirebiliyordu.
                    // (Tüm okumalar yazmalardan önce yapılmalı.)
                    let appointmentRef = null;
                    let appointmentCustomerId = null;
                    if (appointmentId) {
                        const candidateRef = doc(db, "appointments", appointmentId);
                        const appointmentDoc = await transaction.get(candidateRef);
                        if (appointmentDoc.exists()) {
                            if (appointmentDoc.data().workOrderId) {
                                throw new Error("Bu randevudan zaten bir iş emri oluşturulmuş. Randevu detayından mevcut iş emrine gidin.");
                            }
                            appointmentRef = candidateRef;
                            appointmentCustomerId = appointmentDoc.data().customerId || null;
                        }
                    }

                    const customerData = customerDoc.data();

                    const newToplamSatis = (customerData.toplamSatis || 0) + currentToplamTutar;
                    const newToplamOdeme = (customerData.toplamOdeme || 0) + currentOdenenTutar;
                    const newToplamBakiye = (customerData.toplamBakiye || 0) + currentKalanTutar;

                    transaction.update(customerDocRef, {
                        toplamSatis: newToplamSatis,
                        toplamOdeme: newToplamOdeme,
                        toplamBakiye: newToplamBakiye
                    });

                    const newWorkOrderRef = doc(workOrdersColRef);
                    const newWorkOrderData = {
                        ...workOrderData,
                        odenenTutar: currentOdenenTutar,
                        kalanTutar: currentKalanTutar,
                        appointmentId: appointmentId || null,
                        durum: "İşlemde",
                        isEmriTarihi: serverTimestamp()
                    };
                    transaction.set(newWorkOrderRef, newWorkOrderData);

                    // Peşinat, ödemeler tablosunda görünsün diye payments alt-koleksiyonuna da yazılır.
                    // (Aksi halde personel peşinatı kaydedilmemiş sanıp ikinci kez giriyordu -> çift tahsilat.)
                    if (currentOdenenTutar > 0) {
                        const pesinatRef = doc(collection(newWorkOrderRef, "payments"));
                        transaction.set(pesinatRef, {
                            tutar: currentOdenenTutar,
                            odemeYontemi: 'Nakit',
                            aciklama: 'Peşinat (iş emri açılışı)',
                            tarih: serverTimestamp(),
                            musteriId: customer.id,
                            musteriAdSoyad: customer.adSoyad,
                            aracPlaka: vehicle.plaka || ''
                        });
                    }

                    // Randevu durumunu güncelle (referans yukarıda okundu).
                    // Müşteri ön görüşme anında henüz yoksa randevu customerId'siz kaydediliyor;
                    // iş emrinde müşteri kesinleştiği için bu alan burada geriye dönük doldurulur.
                    if (appointmentRef) {
                        const appointmentGuncelleme = {
                            durum: 'Onaylandı',
                            workOrderId: newWorkOrderRef.id
                        };
                        if (!appointmentCustomerId) appointmentGuncelleme.customerId = customer.id;
                        transaction.update(appointmentRef, appointmentGuncelleme);
                    }

                    // Ön görüşme durumunu güncelle
                    if (preInterviewId) {
                        const preRef = doc(db, "preInterviews", preInterviewId);
                        transaction.update(preRef, { durum: 'Müşteriye Dönüştü' });
                    }
                });

                alert("İş emri oluşturuldu ve müşteri bakiyesi güncellendi.");
                // replace: geri tuşu doldurulmuş formu yeniden açmasın (mükerrer kayıt riski).
                navigate('/garaj', { replace: true });
            }
        } catch (e) {
            console.error("İş emri kaydedilirken hata oluştu: ", e);
            alert(`Bir hata oluştu: ${e?.message || e}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom>{isEditMode ? 'İş Emrini Düzenle' : 'Yeni İş Emri Oluştur'}</Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2, mb: 3 }}>
                <FormControl fullWidth><InputLabel>Müşteri *</InputLabel><Select value={customers.find(c => c.id === selectedCustomerId) ? selectedCustomerId : ''} label="Müşteri *" onChange={(e) => { setSelectedCustomerId(e.target.value); setSelectedVehicleId(''); }}>{customers.map(c => (<MenuItem key={c.id} value={c.id}>{c.adSoyad}</MenuItem>))}</Select></FormControl>
                <FormControl fullWidth disabled={!selectedCustomerId}><InputLabel>Araç *</InputLabel><Select value={vehicles.find(v => v.id === selectedVehicleId) ? selectedVehicleId : ''} label="Araç *" onChange={(e) => setSelectedVehicleId(e.target.value)}>{vehicles.map(v => (<MenuItem key={v.id} value={v.id}>{v.plaka}</MenuItem>))}</Select></FormControl>
                <FormControl fullWidth sx={{ minWidth: 180 }}>
                    <InputLabel>Araç Türü</InputLabel>
                    <Select
                        value={aracTuru}
                        label="Araç Türü"
                        onChange={(e) => setAracTuru(e.target.value)}
                        disabled={isEditMode}
                    >
                        <MenuItem value=""><em>Belirtilmedi (taban fiyat)</em></MenuItem>
                        {aracTurleri.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                    </Select>
                </FormControl>
                <DatePicker label="Teslim Tarihi *" value={teslimTarihi} onChange={setTeslimTarihi} sx={{ minWidth: 220 }} />
            </Box>
            {isEditMode && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -2, mb: 2 }}>
                    Araç türü düzenlemede kilitlidir: değiştirilmesi kayıtlı kalem fiyatlarını geriye dönük değiştirirdi.
                </Typography>
            )}

            <Divider sx={{ my: 3 }}><Chip label="Uygulama Seçimi" /></Divider>

            <Stack direction="row" sx={{ overflowX: 'auto', pb: 1, mb: 3, justifyContent: 'center' }}>
                <ToggleButtonGroup
                    color="primary"
                    value={uygulamaTurleri}
                    onChange={handleUygulamaChange}
                    sx={{ justifyContent: 'center' }}
                >
                    {/* GÜNCELLENDİ: Paketler butonu eklendi */}
                    <ToggleButton value="paketler" sx={{ whiteSpace: 'nowrap' }}>Paketler</ToggleButton>
                    <ToggleButton value="camFilmi" sx={{ whiteSpace: 'nowrap' }}>Cam Filmi</ToggleButton>
                    <ToggleButton value="ppf" sx={{ whiteSpace: 'nowrap' }}>PPF</ToggleButton>
                    <ToggleButton value="seramik" sx={{ whiteSpace: 'nowrap' }}>Seramik</ToggleButton>
                    <ToggleButton value="gocuk" sx={{ whiteSpace: 'nowrap' }}>Göçük</ToggleButton>
                    <ToggleButton value="yikama" sx={{ whiteSpace: 'nowrap' }}>Yıkama</ToggleButton>
                    <ToggleButton value="diger" sx={{ whiteSpace: 'nowrap' }}>Diğer</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Stack spacing={2}>
                {/* GÜNCELLENDİ: Paket Seçimi Bölümü */}
                {uygulamaTurleri.includes('paketler') && (
                    <PartSelectionSection
                        title="Paket Seçimi"
                        parts={["Uygulanacak Paket"]} // Paketler tek parça olarak ele alınır
                        services={paketServices}
                        data={paketlerData}
                        setData={setPaketlerData}
                    />
                )}

                {/* vehicleType YALNIZCA yeni iş emrinde geçilir: düzenlemede geçilirse kayıtlı fiyatlar sessizce yeniden hesaplanır. */}
                {uygulamaTurleri.includes('camFilmi') && <PartSelectionSection title="Cam Filmi Detayları" parts={camParts} services={filmServices} data={camFilmiData} setData={setCamFilmiData} vehicleType={isEditMode ? undefined : aracTuru} category="camFilmi" />}
                {uygulamaTurleri.includes('ppf') && <PartSelectionSection title="PPF Kaplama Detayları" parts={ppfParts} services={ppfServices} data={ppfData} setData={setPpfData} vehicleType={isEditMode ? undefined : aracTuru} category="ppf" />}
                {uygulamaTurleri.includes('seramik') && <PartSelectionSection title="Seramik Kaplama Detayları" parts={seramikParts} services={seramikServices} data={seramikData} setData={setSeramikData} vehicleType={isEditMode ? undefined : aracTuru} category="seramik" />}

                {uygulamaTurleri.includes('gocuk') && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Göçük Düzeltme Hizmeti</Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField label="Göçük Fiyatı" type="number" size="small" value={gocukData.fiyat} onChange={e => setGocukData({ ...gocukData, fiyat: Number(e.target.value) })} fullWidth />
                            <TextField label="Açıklama" size="small" value={gocukData.aciklama} onChange={e => setGocukData({ ...gocukData, aciklama: e.target.value })} fullWidth multiline rows={2} />
                        </Stack>
                    </Paper>
                )}
                {uygulamaTurleri.includes('yikama') && <Paper variant="outlined" sx={{ p: 2 }}><Typography variant="subtitle1">Yıkama Hizmeti</Typography><TextField label="Yıkama Fiyatı" type="number" size="small" value={yikamaData.fiyat} onChange={e => setYikamaData({ aktif: true, fiyat: Number(e.target.value) })} sx={{ mt: 2 }} fullWidth /></Paper>}
                {uygulamaTurleri.includes('diger') && <Paper variant="outlined" sx={{ p: 2 }}><Typography variant="subtitle1">Diğer Hizmetler</Typography><Stack spacing={1} sx={{ mt: 1 }}>{digerHizmetler.map((h, i) => <Grid container spacing={1} key={i} alignItems="center"><Grid item xs><TextField label="Açıklama" size="small" fullWidth value={h.aciklama} onChange={e => handleDigerChange(i, 'aciklama', e.target.value)} /></Grid><Grid item xs={3}><TextField label="Fiyat" size="small" type="number" fullWidth value={h.fiyat} onChange={e => handleDigerChange(i, 'fiyat', Number(e.target.value))} /></Grid><Grid item xs="auto"><IconButton disabled={digerHizmetler.length === 1} onClick={() => handleRemoveDiger(i)}><DeleteIcon /></IconButton></Grid></Grid>)}</Stack><Button onClick={handleAddDiger} size="small" sx={{ mt: 1 }}>+ Satır Ekle</Button></Paper>}
            </Stack>

            <Divider sx={{ my: 3 }}><Chip label="Ödeme ve Notlar" /></Divider>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}><TextField label="İş Emri Notları" multiline rows={4} fullWidth value={isEmriNotlari} onChange={e => setIsEmriNotlari(e.target.value)} /></Grid>
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <TextField label="Toplam Tutar (₺) — KDV hariç" value={toplamTutar.toFixed(2)} fullWidth InputProps={{ readOnly: true }} />
                        <TextField
                            label={isEditMode ? 'Ödenen Tutar (₺) — salt okunur' : 'Peşinat / Ödenen Tutar (₺)'}
                            type="number"
                            value={odenenTutar}
                            onChange={e => setOdenenTutar(Number(e.target.value))}
                            InputProps={{ readOnly: isEditMode }}
                            disabled={isEditMode}
                            helperText={isEditMode
                                ? 'Ödeme yalnızca İş Emri Detay > Ödeme Ekle üzerinden girilir.'
                                : 'Girilen tutar ödemeler tablosuna "Peşinat" olarak da kaydedilir.'}
                        />
                        <TextField label="Kalan Tutar (₺)" value={(toplamTutar - odenenTutar).toFixed(2)} fullWidth InputProps={{ readOnly: true }} />
                    </Stack>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="success" size="large" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Değişiklikleri Kaydet' : 'İş Emrini Kaydet')}
                </Button>
            </Box>
        </Paper>
    );
};

export default IsEmriOlustur;